import React from "react";
import { useI18n } from "@/lib/i18n";

const Block = ({ title, children }) => (
  <div className="mb-8">
    <h3 className="font-serif text-xl text-[#001F3F] mb-2">{title}</h3>
    <div className="font-sans text-sm text-[#0A1118] leading-relaxed space-y-2">{children}</div>
  </div>
);

const List = ({ items }) => (
  <ul className="list-disc pl-5 space-y-1">
    {items.map((it, i) => (
      <li key={i}>{it}</li>
    ))}
  </ul>
);

const EN = () => (
  <div data-testid="case-report-guide-en">
    <Block title="Title">
      <p>Must make clear that it is a clinical case. It usually includes the main finding.</p>
      <p className="italic text-gray-600">Example: “Severe hypocalcemia as the initial presentation of…: a case report”.</p>
    </Block>
    <Block title="Abstract">
      <p>Usually structured in:</p>
      <List items={[
        "Background / Introduction: why the case is relevant.",
        "Case presentation: essential patient data, diagnosis, treatment and course.",
        "Conclusion: what lesson the case provides.",
      ]} />
    </Block>
    <Block title="Keywords">
      <p>Generally 3–6. Use MeSH terms whenever possible.</p>
    </Block>
    <Block title="Introduction">
      <p>Very brief, usually 2–4 paragraphs:</p>
      <List items={[
        "What is known about the disease or problem.",
        "What is unusual about this case.",
        "Why it deserves to be published.",
      ]} />
      <p>The last paragraph usually closes with something like: “We present the case of…”</p>
    </Block>
    <Block title="Case Presentation">
      <p>This is the central part. The recommended order is chronological:</p>
      <List items={[
        "Relevant demographic data.",
        "Chief complaint.",
        "Relevant personal and family history.",
        "Present illness.",
        "Physical examination.",
        "Laboratory studies.",
        "Imaging / ancillary studies.",
        "Differential diagnoses.",
        "Diagnostic process.",
        "Final diagnosis.",
        "Treatment / intervention.",
        "Clinical course.",
        "Follow-up.",
        "Final patient status.",
      ]} />
      <p className="italic text-gray-600">Ideally it should read as a timeline: Patient → symptoms → evaluation → findings → diagnosis → treatment → response → follow-up.</p>
    </Block>
    <Block title="Discussion">
      <p>This is where the publication is scientifically justified. A order that works very well:</p>
      <List items={[
        "Paragraph 1: the main message of the case.",
        "Following paragraphs: comparison with the literature.",
        "Then: pathophysiological mechanism that could explain the findings.",
        "Then: differential diagnosis and relevant therapeutic decisions.",
        "Then: what makes this case different from previously published ones.",
        "Finally: limitations and clinical takeaway.",
      ]} />
      <p>Avoid repeating the whole case presentation here.</p>
    </Block>
    <Block title="Conclusion">
      <p>Very brief: usually one or two paragraphs. It should answer:</p>
      <List items={[
        "What do we learn from this case?",
        "What should the clinician remember if they encounter a similar patient?",
      ]} />
    </Block>
    <Block title="Patient Perspective">
      <p>Some journals, especially those following CARE, allow or request a brief patient perspective.</p>
    </Block>
    <Block title="Informed Consent">
      <p className="italic text-gray-600">Example: “Written informed consent was obtained from the patient for publication of this case report and accompanying images.”</p>
    </Block>
    <Block title="Declarations">
      <p>Depending on the journal:</p>
      <List items={[
        "Ethics approval",
        "Consent for publication",
        "Conflict of interest",
        "Funding",
        "Author contributions",
        "Data availability",
      ]} />
    </Block>
    <Block title="References">
      <p>Cited following the journal reference style.</p>
    </Block>
  </div>
);

const ES = () => (
  <div data-testid="case-report-guide-es">
    <Block title="Título">
      <p>Debe dejar claro que se trata de un caso clínico. Suele incluir el hallazgo principal.</p>
      <p className="italic text-gray-600">Ejemplo: “Severe hypocalcemia as the initial presentation of…: a case report”.</p>
    </Block>
    <Block title="Abstract / Resumen">
      <p>Habitualmente estructurado en:</p>
      <List items={[
        "Background / Introduction: por qué el caso es relevante.",
        "Case presentation: datos esenciales del paciente, diagnóstico, tratamiento y evolución.",
        "Conclusion: qué enseñanza aporta el caso.",
      ]} />
    </Block>
    <Block title="Keywords / Palabras clave">
      <p>Generalmente 3–6. Conviene usar términos MeSH cuando sea posible.</p>
    </Block>
    <Block title="Introduction / Introducción">
      <p>Muy breve, normalmente 2–4 párrafos:</p>
      <List items={[
        "Qué se conoce sobre la enfermedad o problema.",
        "Qué tiene de inusual este caso.",
        "Por qué merece ser publicado.",
      ]} />
      <p>El último párrafo suele cerrar con algo parecido a: “We present the case of…”</p>
    </Block>
    <Block title="Case Presentation / Presentación del caso">
      <p>Esta es la parte central. El orden recomendable es cronológico:</p>
      <List items={[
        "Datos demográficos relevantes.",
        "Motivo de consulta.",
        "Antecedentes personales y familiares relevantes.",
        "Enfermedad actual.",
        "Exploración física.",
        "Estudios de laboratorio.",
        "Estudios de imagen/gabinete.",
        "Diagnósticos diferenciales.",
        "Proceso diagnóstico.",
        "Diagnóstico final.",
        "Tratamiento/intervención.",
        "Evolución clínica.",
        "Seguimiento.",
        "Estado final del paciente.",
      ]} />
      <p className="italic text-gray-600">Idealmente debe leerse como una línea de tiempo: Paciente → síntomas → evaluación → hallazgos → diagnóstico → tratamiento → respuesta → seguimiento.</p>
    </Block>
    <Block title="Discussion / Discusión">
      <p>Es donde se justifica científicamente la publicación. Un orden que funciona muy bien es:</p>
      <List items={[
        "Párrafo 1: cuál es el mensaje principal del caso.",
        "Párrafos siguientes: comparación con la literatura.",
        "Después: mecanismo fisiopatológico que podría explicar los hallazgos.",
        "Después: diagnóstico diferencial y decisiones terapéuticas relevantes.",
        "Después: qué hace diferente a este caso respecto a casos previamente publicados.",
        "Finalmente: limitaciones y aprendizaje clínico.",
      ]} />
      <p>Aquí no conviene repetir toda la presentación del caso.</p>
    </Block>
    <Block title="Conclusion / Conclusiones">
      <p>Muy breve: normalmente uno o dos párrafos. Debe responder:</p>
      <List items={[
        "¿Qué aprendemos de este caso?",
        "¿Qué debería recordar el médico si encuentra un paciente similar?",
      ]} />
    </Block>
    <Block title="Patient Perspective">
      <p>Algunas revistas, especialmente las que siguen CARE, permiten o solicitan una breve perspectiva del paciente.</p>
    </Block>
    <Block title="Informed Consent / Consentimiento informado">
      <p className="italic text-gray-600">Ejemplo: “Written informed consent was obtained from the patient for publication of this case report and accompanying images.”</p>
    </Block>
    <Block title="Declarations">
      <p>Dependiendo de la revista:</p>
      <List items={[
        "Ethics approval",
        "Consent for publication",
        "Conflict of interest",
        "Funding",
        "Author contributions",
        "Data availability",
      ]} />
    </Block>
    <Block title="References / Referencias">
      <p>Citadas siguiendo el estilo de referencias de la revista.</p>
    </Block>
  </div>
);

const ReviewEN = () => (
  <div data-testid="review-article-guide-en">
    <Block title="Title">
      <p>Should state the topic and that it is a review (narrative or systematic).</p>
      <p className="italic text-gray-600">Example: “Current management of…: a narrative review”.</p>
    </Block>
    <Block title="Abstract">
      <p>Summary of scope, methods (for systematic reviews) and key findings. Structured or unstructured depending on the review type.</p>
    </Block>
    <Block title="Keywords">
      <p>Generally 3–6, preferably MeSH terms.</p>
    </Block>
    <Block title="Introduction">
      <p>Define the topic, its clinical relevance, the rationale for the review and its objectives/scope.</p>
    </Block>
    <Block title="Methods (systematic reviews / meta-analyses)">
      <List items={[
        "Databases searched and search strategy.",
        "Inclusion and exclusion criteria.",
        "Study selection and data extraction process.",
        "Risk-of-bias assessment / PRISMA flow when applicable.",
      ]} />
      <p>Narrative reviews may omit a formal Methods section.</p>
    </Block>
    <Block title="Main Body / Thematic Sections">
      <p>Organize the evidence into logical subtopics. Synthesize and compare findings rather than listing studies one by one.</p>
    </Block>
    <Block title="Discussion">
      <List items={[
        "Integrate the main findings.",
        "Strengths and limitations of the available evidence.",
        "Clinical implications.",
        "Knowledge gaps and directions for future research.",
      ]} />
    </Block>
    <Block title="Conclusion">
      <p>Concise take-home message supported by the reviewed evidence.</p>
    </Block>
    <Block title="Declarations">
      <List items={["Funding", "Conflict of interest", "Author contributions", "Data availability"]} />
    </Block>
    <Block title="References">
      <p>Comprehensive and up to date, following the journal reference style.</p>
    </Block>
  </div>
);

const ReviewES = () => (
  <div data-testid="review-article-guide-es">
    <Block title="Título">
      <p>Debe indicar el tema y que se trata de una revisión (narrativa o sistemática).</p>
      <p className="italic text-gray-600">Ejemplo: “Current management of…: a narrative review”.</p>
    </Block>
    <Block title="Abstract / Resumen">
      <p>Resumen del alcance, los métodos (en revisiones sistemáticas) y los hallazgos principales. Estructurado o no según el tipo de revisión.</p>
    </Block>
    <Block title="Keywords / Palabras clave">
      <p>Generalmente 3–6, preferentemente términos MeSH.</p>
    </Block>
    <Block title="Introduction / Introducción">
      <p>Define el tema, su relevancia clínica, la justificación de la revisión y sus objetivos/alcance.</p>
    </Block>
    <Block title="Methods / Métodos (revisiones sistemáticas / metaanálisis)">
      <List items={[
        "Bases de datos consultadas y estrategia de búsqueda.",
        "Criterios de inclusión y exclusión.",
        "Proceso de selección de estudios y extracción de datos.",
        "Evaluación del riesgo de sesgo / diagrama PRISMA cuando aplique.",
      ]} />
      <p>Las revisiones narrativas pueden omitir una sección formal de Métodos.</p>
    </Block>
    <Block title="Cuerpo principal / Secciones temáticas">
      <p>Organiza la evidencia en subtemas lógicos. Sintetiza y compara los hallazgos en lugar de enumerar los estudios uno por uno.</p>
    </Block>
    <Block title="Discussion / Discusión">
      <List items={[
        "Integra los hallazgos principales.",
        "Fortalezas y limitaciones de la evidencia disponible.",
        "Implicaciones clínicas.",
        "Vacíos de conocimiento y líneas de investigación futuras.",
      ]} />
    </Block>
    <Block title="Conclusion / Conclusiones">
      <p>Mensaje final conciso, respaldado por la evidencia revisada.</p>
    </Block>
    <Block title="Declarations">
      <List items={["Funding", "Conflict of interest", "Author contributions", "Data availability"]} />
    </Block>
    <Block title="References / Referencias">
      <p>Completas y actualizadas, siguiendo el estilo de referencias de la revista.</p>
    </Block>
  </div>
);

const SurgEN = () => (
  <div data-testid="surgical-technique-guide-en">
    <Block title="Title">
      <p>Name the procedure and make clear it is a technique description.</p>
      <p className="italic text-gray-600">Example: “Laparoscopic … : surgical technique”.</p>
    </Block>
    <Block title="Abstract">
      <p>Brief summary of the technique, its purpose and the clinical context.</p>
    </Block>
    <Block title="Keywords">
      <p>Generally 3–6, preferably MeSH terms.</p>
    </Block>
    <Block title="Introduction">
      <p>Clinical problem, rationale for the technique, existing approaches and what this technique adds.</p>
    </Block>
    <Block title="Surgical Technique">
      <p>This is the central section. A useful order is:</p>
      <List items={[
        "Indications and contraindications.",
        "Preoperative preparation and work-up.",
        "Patient positioning and anesthesia.",
        "Step-by-step description of the procedure.",
        "Instruments, devices and materials used.",
        "Intraoperative tips, tricks and pitfalls.",
        "Closure and postoperative care.",
      ]} />
    </Block>
    <Block title="Discussion">
      <List items={[
        "Advantages and limitations of the technique.",
        "Potential complications and how to avoid them.",
        "Comparison with alternative techniques.",
        "Learning curve and reproducibility.",
      ]} />
    </Block>
    <Block title="Conclusion">
      <p>Concise summary of the technique’s value and applicability.</p>
    </Block>
    <Block title="Declarations">
      <p className="italic text-gray-600">Include written informed consent for any identifiable images, plus funding and conflict of interest.</p>
    </Block>
    <Block title="References">
      <p>Cited following the journal reference style.</p>
    </Block>
  </div>
);

const SurgES = () => (
  <div data-testid="surgical-technique-guide-es">
    <Block title="Título">
      <p>Nombra el procedimiento y deja claro que es la descripción de una técnica.</p>
      <p className="italic text-gray-600">Ejemplo: “Laparoscopic … : surgical technique”.</p>
    </Block>
    <Block title="Abstract / Resumen">
      <p>Resumen breve de la técnica, su propósito y el contexto clínico.</p>
    </Block>
    <Block title="Keywords / Palabras clave">
      <p>Generalmente 3–6, preferentemente términos MeSH.</p>
    </Block>
    <Block title="Introduction / Introducción">
      <p>Problema clínico, justificación de la técnica, abordajes existentes y qué aporta esta técnica.</p>
    </Block>
    <Block title="Surgical Technique / Técnica quirúrgica">
      <p>Es la sección central. Un orden útil es:</p>
      <List items={[
        "Indicaciones y contraindicaciones.",
        "Preparación preoperatoria y estudios.",
        "Posición del paciente y anestesia.",
        "Descripción paso a paso del procedimiento.",
        "Instrumentos, dispositivos y materiales utilizados.",
        "Consejos, trucos y errores a evitar durante la cirugía.",
        "Cierre y cuidados postoperatorios.",
      ]} />
    </Block>
    <Block title="Discussion / Discusión">
      <List items={[
        "Ventajas y limitaciones de la técnica.",
        "Posibles complicaciones y cómo evitarlas.",
        "Comparación con técnicas alternativas.",
        "Curva de aprendizaje y reproducibilidad.",
      ]} />
    </Block>
    <Block title="Conclusion / Conclusiones">
      <p>Resumen conciso del valor y la aplicabilidad de la técnica.</p>
    </Block>
    <Block title="Declarations">
      <p className="italic text-gray-600">Incluye consentimiento informado por escrito para cualquier imagen identificable, además de financiación y conflicto de interés.</p>
    </Block>
    <Block title="References / Referencias">
      <p>Citadas siguiendo el estilo de referencias de la revista.</p>
    </Block>
  </div>
);

const TypeSection = ({ testId, title, es, EsComp, EnComp }) => (
  <section data-testid={testId} className="mb-14">
    <h3 className="font-serif text-2xl text-[#001F3F] mb-1">{title}</h3>
    <div className="w-10 h-[2px] bg-[#2BA6A6] mb-6"></div>
    {es ? <EsComp /> : <EnComp />}
  </section>
);

export default function CaseReportGuide() {
  const { lang } = useI18n();
  const es = lang === "es";
  return (
    <div className="max-w-3xl mx-auto px-6 mt-16 pt-12 border-t border-gray-200" data-testid="manuscript-type-guides">
      <div className="mb-10">
        <div className="text-[11px] font-sans tracking-[0.2em] uppercase font-semibold text-[#2BA6A6] mb-2">
          {es ? "Guías por tipo de manuscrito" : "Guides by manuscript type"}
        </div>
        <h2 className="font-serif text-3xl text-[#001F3F] tracking-tight">
          {es ? "Cómo estructurar tu manuscrito" : "How to structure your manuscript"}
        </h2>
        <div className="mt-3 w-12 h-[2px] bg-[#2BA6A6]"></div>
      </div>
      <TypeSection testId="case-report-guide" title={es ? "Case Report / Reporte de caso" : "Case Report"} es={es} EsComp={ES} EnComp={EN} />
      <TypeSection testId="review-article-guide" title={es ? "Review Article / Artículo de revisión" : "Review Article"} es={es} EsComp={ReviewES} EnComp={ReviewEN} />
      <TypeSection testId="surgical-technique-guide" title={es ? "Surgical Technique / Técnica quirúrgica" : "Surgical Technique"} es={es} EsComp={SurgES} EnComp={SurgEN} />
    </div>
  );
}
